import React, { useState } from "react";
import { Icon } from "../icons";
import { fmtDate, fmtReminderWhen } from "../lib/format";
import { hourlyCalc, money } from "../lib/hourly";
import { ReadField, SaveButton, SectionLabel } from "./fields";

/** The orange banner on an hourly job: how it is billed, at a glance. */
const HourlyBanner = ({ job }) => {
  const c = hourlyCalc(job);
  if (!c) return null;
  return (
    <div className="hourly-banner">
      <div className="hb-icon">
        <Icon name="clock" />
      </div>
      <div>
        <div className="hb-title">HOURLY JOB</div>
        <div className="hb-sub">
          {c.movers} movers + truck · {money(c.rate)}/hr · billed on actual working hours
        </div>
      </div>
    </div>
  );
};

export const TabSummary = ({ job, isLD, actions }) => {
  const [remText, setRemText] = useState("");
  const [remDate, setRemDate] = useState("");
  const [remTime, setRemTime] = useState("");
  const packingLine = job.additionalServices.some((s) => /pack/i.test(s))
    ? "Full packing service included"
    : "No packing service on file";
  // Proposal / pickup / delivery / customer-service notes from the lead, grouped by the API.
  // Folded by default: Miles (2026-10-18) found them taking half the screen.
  const salesNoteGroups = (Array.isArray(job.salesNoteGroups) ? job.salesNoteGroups : []).filter(
    (g) => g && Array.isArray(g.notes) && g.notes.length
  );
  const salesNoteCount = salesNoteGroups.reduce((n, g) => n + g.notes.length, 0);
  const [notesOpen, setNotesOpen] = useState(false);
  // Real coverage from the file (an insurance line or the additional insurance charge),
  // instead of the prototype's fixed "Full replacement coverage is included" sentence.
  const covered = Boolean(job.coverageIncluded) || Number(job.replacementCoverageAmount) > 0;
  const keyFeatures = Array.isArray(job.additionalServices) ? job.additionalServices.filter(Boolean) : [];
  const coverageLine = covered
    ? "Replacement coverage: Included" + (job.coverageLine ? ` (${job.coverageLine})` : "")
    : "Replacement coverage: Not included";

  return (
    <>
      <div className="ai-summary">
        <Icon name="sparkle" />
        <div>
          <strong>AI summary — extracted from Zoho</strong>
          <ul style={{ margin: "6px 0 0 16px", padding: 0, lineHeight: 1.75 }}>
            <li>{job.clientName}</li>
            <li>Looking to move on {fmtDate(job.moveDate)}</li>
            <li>
              This job is scheduled to take {job.jobDurationDays} day
              {job.jobDurationDays > 1 ? "s" : ""}
            </li>
            <li>{packingLine}</li>
            <li>{coverageLine}</li>
            <li>Total price of the move: ${job.price}</li>
            <li>
              Notes from Sales:{" "}
              <span className="pre-wrap">{(job.salesNotes || "").trim() || (salesNoteGroups.length ? "" : "—")}</span>
              {salesNoteCount ? (
                <button
                  type="button"
                  className="btn small sales-notes-toggle"
                  aria-expanded={notesOpen}
                  onClick={() => setNotesOpen((v) => !v)}
                >
                  {notesOpen
                    ? "Hide sales notes"
                    : `Show ${salesNoteCount} sales note${salesNoteCount > 1 ? "s" : ""} (proposal, pickup, delivery, customer service)`}
                </button>
              ) : null}
              {notesOpen && salesNoteGroups.map((g) => (
                <div className="sales-note-group" key={g.type}>
                  <strong>{g.label}</strong>
                  <ul>
                    {g.notes.map((n, i) => (
                      <li key={`${g.type}-${i}`}>
                        <span className="pre-wrap">{n.text}</span>
                        {(n.author || n.date) && (
                          <span className="sales-note-meta">
                            {" — "}
                            {[n.author, n.date ? fmtDate(n.date) : ""].filter(Boolean).join(", ")}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </li>
          </ul>
        </div>
      </div>

      <HourlyBanner job={job} />

      <SectionLabel icon="clipboard">Key Features from Zoho</SectionLabel>
      {/* The services booked on the lead in Zoho. The prototype's fixed "Guaranteed delivery" /
          "AP crew" chips showed on every job; Miles asked where they came from (2026-10-18). */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 4 }}>
        {keyFeatures.length ? (
          keyFeatures.map((s, i) => (
            <span className="badge plain" key={`${s}-${i}`}>
              {s}
            </span>
          ))
        ) : (
          <span className="meta" style={{ color: "var(--ink-500)", fontSize: 11 }}>
            No extra services on the file
          </span>
        )}
      </div>
      <div
        className="meta"
        style={{ color: "var(--ink-500)", fontSize: 11, marginBottom: 12 }}
      >
        Source of the job: {job.source}
      </div>

      <SectionLabel icon="clipboard">Job details (from Zoho)</SectionLabel>
      <div className="field-row">
        <ReadField label="Name">{job.clientName}</ReadField>
        <ReadField label="Phone">{job.phone}</ReadField>
        <ReadField label="Email">{job.email}</ReadField>
        <ReadField label="Source">{job.source}</ReadField>
        <ReadField label="Estimated price">${job.price}</ReadField>
        <ReadField label="Contract signed">{job.contractSigned ? "Yes" : "No"}</ReadField>
        <ReadField label="Move date">{fmtDate(job.moveDate)}</ReadField>
        <ReadField label="Packing date">{fmtDate(job.packingDate)}</ReadField>
      </div>

      {isLD ? (
        <>
          <SectionLabel icon="truck">
            Delivery &amp; Storage (Long Distance-specific)
          </SectionLabel>
          <div className="field-row">
            <ReadField label="Delivery window start (first ready date, Zoho)">
              {fmtDate(job.deliveryWindowStart)}
            </ReadField>
            <ReadField label="Delivery window end (Zoho)">
              {fmtDate(job.deliveryWindowEnd)}
            </ReadField>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Storage required / LTS</label>
              <div className="val" style={{ marginBottom: 6 }}>
                {job.storageRequired || "—"}
              </div>
              {job.storageNotes ? (
                <div
                  className="meta"
                  style={{ color: "var(--ink-500)", fontSize: 11 }}
                >
                  {job.storageNotes}
                </div>
              ) : null}
            </div>
            <ReadField label="Shuttle included">{job.shuttleIncluded || "—"}</ReadField>
          </div>
        </>
      ) : null}

      <SectionLabel icon="bell">Reminder</SectionLabel>
      {(job.reminders || []).length ? (
        job.reminders.map((r, i) => (
          <div className="reminder-item" key={i}>
            {r.text}
            <div className="meta">{fmtReminderWhen(r)}</div>
          </div>
        ))
      ) : (
        <div className="panel-empty">No reminders set.</div>
      )}
      <div className="field-row">
        <input
          type="text"
          placeholder="Reminder text"
          value={remText}
          onChange={(e) => setRemText(e.target.value)}
        />
        <div className="reminder-when">
          <input
            type="date"
            aria-label="Reminder date"
            value={remDate}
            onChange={(e) => setRemDate(e.target.value)}
          />
          <input
            type="time"
            aria-label="Reminder time"
            value={remTime}
            onChange={(e) => setRemTime(e.target.value)}
          />
        </div>
      </div>
      <div className="toolbar-row">
        <SaveButton
          small={false}
          idleIcon="bell"
          label="Add reminder"
          savedLabel="Reminder saved"
          disabled={!remText.trim()}
          onSave={() => {
            if (!remText.trim()) return false;
            actions.addJobReminder(job.id, remText.trim(), remDate, remTime);
            setRemText("");
            setRemDate("");
            setRemTime("");
          }}
        />
      </div>

      <SectionLabel icon="reply">Client Responses</SectionLabel>
      {job.responses.length ? (
        <div className="field">
          {job.responses.map((r, i) => (
            <React.Fragment key={i}>
              {i > 0 ? <div style={{ height: 8 }} /> : null}
              <div
                className="val"
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                  fontWeight: 500,
                  lineHeight: 1.6,
                }}
              >
                <Icon name="msg" />
                <span>{r}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="panel-empty">
          No responses recorded — MC will see the latest client reply here (email or
          text).
        </div>
      )}
    </>
  );
};
